import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Canvas, useThree } from '@react-three/fiber';
import { Float, Html, OrbitControls } from '@react-three/drei';
import { Phone, Mail, MapPin, Send, MessageCircle, Globe } from 'lucide-react';
import * as THREE from 'three';

// 3D Contact Background Elements
const Contact3DElements = () => {
  const { scene } = useThree();

  useEffect(() => {
    scene.fog = new THREE.Fog('#0f172a', 1, 15);
  }, [scene]);

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <pointLight position={[-5, -5, -5]} intensity={0.6} color="#3b82f6" />
      <pointLight position={[5, -5, -5]} intensity={0.6} color="#8b5cf6" />

      {/* Floating Contact Icons */}
      <Float speed={1.5}>
        <Html position={[-4, 2, 0]} center>
          <IconCircle bg="rgba(59, 130, 246, 0.2)" border="rgba(59, 130, 246, 0.3)">
            <Phone size={24} color="#3b82f6" />
          </IconCircle>
        </Html>
      </Float>

      <Float speed={2}>
        <Html position={[4, -1, 2]} center>
          <IconCircle bg="rgba(139, 92, 246, 0.2)" border="rgba(139, 92, 246, 0.3)">
            <Mail size={24} color="#8b5cf6" />
          </IconCircle>
        </Html>
      </Float>

      <Float speed={1.2}>
        <Html position={[0, 3, -2]} center>
          <IconCircle bg="rgba(34, 197, 94, 0.2)" border="rgba(34, 197, 94, 0.3)">
            <MapPin size={24} color="#22c55e" />
          </IconCircle>
        </Html>
      </Float>
    </>
  );
};

// Styled Icon for 3D elements
const IconCircle = styled.div`
  background: ${(p) => p.bg};
  border-radius: 50%;
  padding: 20px;
  border: 2px solid ${(p) => p.border};
  backdrop-filter: blur(10px);
`;

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(10px); }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
  100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
`;

// Layout styles
const ContactContainer = styled.div`
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a, #334155);
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const ContentOverlay = styled.div`
  position: relative;
  z-index: 10;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ContactCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(10px);
`;

const ContactIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${(p) => p.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  animation: ${floatAnimation} 3s ease-in-out infinite;
`;

const FormSection = styled(motion.div)`
  margin-top: 4rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormInput = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1rem;
  color: white;
`;

const FormTextarea = styled.textarea`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1rem;
  color: white;
  min-height: 120px;
`;

const SubmitButton = styled(motion.button)`
  margin-top: 1rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 10px;
  color: white;
  font-weight: 600;
  border: none;
`;

const MapSection = styled(motion.div)`
  margin-top: 4rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1rem;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 300px;

  @media (min-width: 768px) {
    height: 400px;
  }

  @media (min-width: 1024px) {
    height: 500px;
  }
`;

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 15px;
  }
`;

const FloatingContactButton = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  animation: ${pulseAnimation} 2s infinite;
  z-index: 1000;
`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      alert('Message sent!');
      setIsSubmitting(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 2000);
  };

  return (
    <ContactContainer>
      <CanvasContainer>
        <Canvas camera={{ position: [0, 0, 8], fov: 75 }}>
          <Contact3DElements />
          <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.2} />
        </Canvas>
      </CanvasContainer>

      <ContentOverlay>
        <ContentWrapper>
          {/* Contact Info Cards */}
          <ContactGrid>
            <ContactCard>
              <ContactIcon gradient="linear-gradient(135deg, #3b82f6, #1d4ed8)">
                <Phone color="white" />
              </ContactIcon>
              <h3>Call Us</h3>
              <p>+91-8787044997</p>
            </ContactCard>

            <ContactCard>
              <ContactIcon gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)">
                <Mail color="white" />
              </ContactIcon>
              <h3>Email Us</h3>
              <p>support@zyntro.in</p>
            </ContactCard>

            <ContactCard>
              <ContactIcon gradient="linear-gradient(135deg, #22c55e, #16a34a)">
                <MapPin color="white" />
              </ContactIcon>
              <h3>Visit Us</h3>
              <p>Registered Address:-  Sant Ravidas Nagar, Bhadohi</p>
              <p>Office Address:-  S-2/1A-81-R-I/101, Tagore Town Colony, Orderly Bazar Varanasi-221002</p>
            </ContactCard>
          </ContactGrid>

          {/* Contact Form */}
          <FormSection>
            <form id="contact-form" onSubmit={handleSubmit}>
              <FormGrid>
                <FormInput name="name" placeholder="Full Name" value={formData.name} onChange={handleInputChange} required />
                <FormInput type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required />
                <FormInput name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} />
                <FormInput name="subject" placeholder="Subject" value={formData.subject} onChange={handleInputChange} required />
                <FormTextarea name="message" placeholder="Message" value={formData.message} onChange={handleInputChange} required style={{ gridColumn: '1 / -1' }} />
              </FormGrid>
              <SubmitButton type="submit">{isSubmitting ? 'Sending...' : 'Send Message'}</SubmitButton>
            </form>
          </FormSection>

          {/* Responsive Map */}
          <MapSection>
            <MapPlaceholder>
              <MapWrapper>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d57671.13598029828!2d82.49049340360577!3d25.389877359517556!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x398fde0eeba498d3%3A0xfabbe137e48d4bf4!2sBhadohi%20Nagar%20Palika%2C%20Uttar%20Pradesh%20221401%2C%20India!5e0!3m2!1sen!2sus!4v1754481641477!5m2!1sen!2sus"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                ></iframe>
              </MapWrapper>
            </MapPlaceholder>
          </MapSection>
        </ContentWrapper>
      </ContentOverlay>

      {/* Floating Button */}
      <FloatingContactButton
        onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <MessageCircle size={24} color="white" />
      </FloatingContactButton>
    </ContactContainer>
  );
};

export default ContactSection;
